import { enemyByKind } from "./enemy-defs.js";
/**
 * Game HUD panels: party status, connection/floor status, the always-visible
 * Abilities and Inventory menus, and the floor-complete banner. All DOM.
 *
 * The Inventory menu (M4) is an RS-style 4×4 baseline grid
 * (packages/client/src/inventory-grid.ts — pure layout). Its cells shrink to
 * fit the available right-rail height. Cells are the interaction surface:
 * left-click = primary action, right-click = context menu, and drag = reorder
 * or drop. Rebuilt only when entry data changes; inventory presses survive
 * a rebuild, and equipment acts on press rather than waiting for click.
 */
import { CLASS_DEFS, EQUIP_SLOTS, SLOT_LABELS, consumableById, itemByKind } from "@pd/shared";
import type { ConsumableDef, EquipSlot, GearCombatStats, HeroClass, ItemDef, Observation, PocketItem } from "@pd/shared";
import { keyLetter, playerColor } from "./actions.js";
import { dockIconSvg, buffIconSvg } from "./ability-icons.js";
import type { DockIcon } from "./ability-icons.js";
import type { BuffEntry } from "./buff-bar.js";
import { abilityHint, createAbilityTooltip, updateAbilityTooltip } from "./ability-tooltip.js";
import { attachItemStats, gearStatsPanel, itemEfficiency } from "./gear-stats.js";
import { equipmentSlotIcon } from "./equipment-icons.js";
import { combatFocus, floorTime } from "./fightHud.js";
import { gridCells, reorderEntries } from "./inventory-grid.js";
import { traceItemClick } from "./item-click-debug.js";

export interface InventoryEntry {
  label: string;
  sub?: string;
  primary: string;
  disabled?: boolean;
  /** Item id for the cell icon ("key", "gatestone-group",
   *  "gatestone-personal") — resolved by onDrawIcon when set. */
  icon?: string;
  /** Owner player id for a carried stone — the icon's per-tint identity. */
  ownerId?: number;
  /** Held key entity id (reorder payload). */
  keyId?: number;
  /** Carried stone id (reorder payload + drag-out drop). */
  stoneId?: number;
  /** Pocket item kind (its catalog id — shared/items.ts for equipment,
   *  shared/consumables.ts for consumables). Sits in the grid: left-click
   *  equips (equipment) or uses (consumables), drag reorders it, and
   *  drag-out (or the menu's Drop) drops it at your feet (DropItem — items
   *  CAN leave the party now, amended 2026-10-25). */
  itemKind?: number;
  /** The POCKET INDEX of a pocket item (me.items[i] — the DropItem op
   *  names the slot, like UseItem). */
  itemSlot?: number;
  /** Which catalog `itemKind` is an id in (the pocket is class-tagged,
   *  design/loot.md) — the reorder payload carries the pair. */
  itemCls?: "equipment" | "consumable";
  itemDef?: ItemDef;
  efficiencyPct?: number;
  onPrimary?: () => void;
  menu: { label: string; run?: () => void }[];
}

/** One slot of the Equipment panel's anatomical loadout. */
export interface EquipmentSlot {
  slot: EquipSlot;
  label: string;
  /** The kind worn in the slot, or null when the slot is empty. */
  itemKind: number | null;
  itemDef?: ItemDef;
  efficiencyPct?: number;
}

export interface AbilityEntry {
  id: string;
  label: string;
  icon: DockIcon;
  sub?: string;
  enabled: boolean;
  group: "class" | "gatestone" | "map";
  key?: string;
  cooldownPct?: number;
  cooldownSeconds?: number;
  active?: boolean;
  armed?: boolean;
  onUse?: () => void;
}

/**
 * The party panel's keyrace row (keyracing.md D2, 11-15) — the whole of what
 * "armed" and "racing" look like to a player. Those two words are registry
 * states and appear nowhere here: the row says the race is on, how long a host
 * has to start it, and which sides are already down there.
 *
 * `startsInMs` is the server's remaining START window, measured at broadcast and
 * never a timestamp (the client's clock is not the server's). It is display
 * only: the forfeit is the server's, and 0 with `running` false means the row is
 * showing a race whose window is not running for anyone.
 */
export interface RaceRowInfo {
  /** The row's clock, as text the caller composed (the row does not know whether
   *  it is counting a start window or a challenge window) and its tone. Kept out
   *  of the rebuild signature — see `setRaceClock`. */
  clock: { text: string; tone: "normal" | "low" | "done" };
  /** My side and theirs. `name` is the OTHER party's name for `theirs` (what a
   *  player reads as "theirs"), so the caller resolves both; the row never
   *  guesses. `inIt` = that side's host has opened its race door. */
  mine: { name: string; inIt: boolean };
  theirs: { name: string; inIt: boolean };
  /** Only the host of a side can open its door, and saying so to anybody else
   *  would be a promise the server refuses. */
  iAmHost: boolean;
  /** An OPEN challenge naming my party (D2). The consent window is dismissible
   *  and dismissal is NOT an answer — silence is not consent, and expiry is the
   *  issuer's out — so the row is where a challenge stays visible after the
   *  window is closed, with the verb that opens it again. Absent = nothing to
   *  vote on. */
  challenge?: { text: string; onReview: () => void };
}

export interface PartyActions {
  meId: number;
  leaveLabel?: string;
  onLeave?: () => void;
  onPromote?: (id: number) => void;
  openMenu?: (x: number, y: number, title: string, items: { label: string; run: () => void }[]) => void;
  /** The town's NO-PARTY face (keyracing.md A1). Parties are explicit now and
   *  the door requires one, so "you are in no party" is the town's default
   *  state — and a lone row with a crown on it reads as "party of one, you
   *  lead it", which is the opposite fact. Passing `onCreate` says: this list
   *  is NOT a party, say so, and offer the verb that changes it. The floor
   *  never passes it (its list is always a real party), so it can't show up
   *  mid-run. */
  createLabel?: string;
  onCreate?: () => void;
}

export class Hud {
  private partyEl: HTMLDivElement;
  /** renderParty's rebuild signature (see the method — keys change per
   *  tick; the list DOM must not churn with them). */
  private partySig = "";
  private equipmentEl: HTMLDivElement;
  private abilitiesEl: HTMLDivElement;
  /** The buff bar: the hero's active timed effects, floating over the canvas
   *  at its top-right (clear of chat at every width and of the centred
   *  connection notice). */
  private buffBarEl: HTMLDivElement;
  private buffSig = "";
  private buffTiles: HTMLDivElement[] = [];
  private buffTips: HTMLElement[] = [];
  private inventoryEl: HTMLDivElement;
  private bannerEl: HTMLDivElement;
  private combatNameEl: HTMLSpanElement | null = null;
  private combatIdleEl: HTMLSpanElement | null = null;
  private heroHpEl: HTMLDivElement | null = null;
  private enemyHpEl: HTMLDivElement | null = null;
  private enemyRowEl: HTMLDivElement | null = null;
  private timerEl: HTMLSpanElement | null = null;
  /** Header count badges (party online/total, inventory item count). */
  private partyCountEl: HTMLSpanElement;
  private invCountEl: HTMLSpanElement;
  /** Signature of the last-rendered panel contents (JSON of entry data). The
   *  panels are rebuilt ONLY when this changes — rebuilding every tick would
   *  destroy the button elements between mousedown and mouseup, and the
   *  browser then fires `click` on the panel (no handler), swallowing the
   *  click silently (the gatestone buttons' bug). */
  private abilitySig = "";
  private currentAbilities: AbilityEntry[] = [];
  private abilityButtons: HTMLButtonElement[] = [];
  private equipmentSig = "";
  private inventorySig = "";
  private currentEquipment: EquipmentSlot[] = [];
  private currentEquipmentStats?: GearCombatStats;
  /** The entries backing the current grid (stashed for drag reorder). */
  private currentEntries: InventoryEntry[] = [];
  /** Active pointer-drag state (null when no item is being dragged). */
  private drag:
    | {
        source: HTMLElement;
        entry: InventoryEntry;
        fromIndex: number;
        entriesAtPress: readonly InventoryEntry[];
        bounds: DOMRect;
        pointerId: number;
        startedAt: number;
        startX: number;
        startY: number;
        lastX: number;
        lastY: number;
        active: boolean;
        inPanel: boolean;
        ghost: HTMLElement | null;
        dropTarget: HTMLElement | null;
      }
    | null = null;
  private clearDragListeners: (() => void) | null = null;

  constructor(root: HTMLElement, floor = false) {
    // Party and Status share the top-right corner, stacked in one column
    // (separate absolutely-positioned panels would overlap).
    const topRight = document.createElement("div");
    topRight.id = "top-right";
    root.appendChild(topRight);
    const party = this.panel(topRight, "party-panel", "Party");
    this.partyEl = party.body;
    this.partyCountEl = party.badge;
    // The keyrace row hangs under the party LIST, inside the same panel: it is a
    // fact about the party, and the panel is the one surface shared by the town
    // and a floor (keyracing.md D2). Appending to `partyEl` is not an option —
    // renderParty rebuilds that element's children wholesale every roster change.
    this.partyPanelEl = party.el;
    // The signed-in workspace has one stable right-rail hierarchy in both
    // the hub and a floor: party, gear, inventory. The compact diagnostic /
    // buff status stays last so it never breaks that player-facing order.
    this.equipmentEl = this.panel(topRight, "equipment-panel", "Equipment").body;
    // The floor's bottom black strip holds fight state, the shared dock and
    // elapsed time. The hub mounts the same dock in the same position.
    const bottom = document.createElement("div");
    bottom.id = "bottom-dock";
    root.appendChild(bottom);
    if (floor) {
      const combat = document.createElement("div");
      combat.id = "combat-summary";
      combat.className = "bottom-widget";
      const heading = document.createElement("div");
      heading.className = "combat-heading";
      heading.textContent = "Combat";
      const hero = this.hpRow("You");
      const enemy = this.hpRow("Enemy");
      enemy.row.classList.add("enemy");
      enemy.row.hidden = true;
      const idle = document.createElement("span");
      idle.className = "combat-idle";
      idle.textContent = "No enemy engaged";
      combat.append(heading, hero.row, enemy.row, idle);
      bottom.appendChild(combat);
      this.combatNameEl = enemy.label;
      this.combatIdleEl = idle;
      this.heroHpEl = hero.track;
      this.enemyHpEl = enemy.track;
      this.enemyRowEl = enemy.row;

      const timer = document.createElement("div");
      timer.id = "floor-timer";
      timer.className = "bottom-widget";
      const timerLabel = document.createElement("span");
      timerLabel.className = "floor-timer-label";
      timerLabel.textContent = "Floor time";
      this.timerEl = document.createElement("span");
      this.timerEl.className = "floor-timer-value";
      this.timerEl.textContent = "00:00";
      timer.append(timerLabel, this.timerEl);
      bottom.appendChild(timer);
    }
    // Abilities are a bare horizontal slot row, no panel-head/body.
    this.abilitiesEl = document.createElement("div");
    this.abilitiesEl.id = "abilities-panel";
    bottom.appendChild(this.abilitiesEl);
    const inv = this.panel(topRight, "inventory-panel", "Inventory");
    this.inventoryEl = inv.body;
    this.invCountEl = inv.badge;
    // The buff bar is mounted on the game root (not inside the dock) so it
    // floats over the canvas; it starts hidden and stays hidden while the
    // hero has nothing active.
    this.buffBarEl = document.createElement("div");
    this.buffBarEl.id = "buff-bar";
    this.buffBarEl.hidden = true;
    root.appendChild(this.buffBarEl);
    this.bannerEl = document.createElement("div");
    this.bannerEl.id = "banner";
    root.appendChild(this.bannerEl);
  }

  /** Build a panel: a fixed title header (never cleared by a render) plus a
   *  body that renderX owns. Panels used to be a bare <h3> + children and
   *  renderX did `el.textContent = ""`, which also wiped the title — every
   *  panel rendered headerless. */
  private panel(
    parent: HTMLElement,
    id: string,
    title: string,
  ): { el: HTMLDivElement; body: HTMLDivElement; badge: HTMLSpanElement } {
    const el = document.createElement("div");
    el.id = id;
    el.className = "panel";
    const head = document.createElement("div");
    head.className = "panel-head";
    const h = document.createElement("h3");
    h.textContent = title;
    const badge = document.createElement("span");
    badge.className = "panel-badge";
    head.append(h, badge);
    const body = document.createElement("div");
    body.className = "panel-body";
    el.append(head, body);
    parent.appendChild(el);
    return { el, body, badge };
  }

  setTick(tick: number): void {
    this.tick = tick;
    if (this.timerEl) this.timerEl.textContent = floorTime(tick);
  }

  /** The bottom-left combat readout projects the latest observation only. */
  renderCombat(obs: Observation, playerId: number): void {
    if (!this.heroHpEl || !this.enemyHpEl || !this.enemyRowEl || !this.combatNameEl || !this.combatIdleEl) return;
    const me = obs.players.find((p) => p.id === playerId);
    if (!me) return; // the leaver's last tick has no player row
    this.setHp(this.heroHpEl, me.hp, me.maxHp);
    const enemy = combatFocus(obs, playerId);
    this.enemyRowEl.hidden = enemy === null;
    this.combatIdleEl.hidden = enemy !== null;
    if (enemy) {
      this.combatNameEl.textContent = enemyByKind(enemy.kind)?.name ?? enemy.kind;
      this.setHp(this.enemyHpEl, enemy.hp, enemy.maxHp);
    }
  }

  private hpRow(name: string): { row: HTMLDivElement; label: HTMLSpanElement; track: HTMLDivElement } {
    const row = document.createElement("div");
    row.className = "combat-hp-row";
    const label = document.createElement("span");
    label.className = "combat-name";
    label.textContent = name;
    const track = document.createElement("div");
    track.className = "combat-hp-track";
    track.setAttribute("role", "progressbar");
    track.setAttribute("aria-label", `${name} health`);
    const fill = document.createElement("div");
    fill.className = "combat-hp-fill";
    const value = document.createElement("span");
    value.className = "combat-hp-value";
    track.append(fill, value);
    row.append(label, track);
    return { row, label, track };
  }

  private setHp(track: HTMLDivElement, hp: number, maxHp: number): void {
    const safeMax = Math.max(1, maxHp);
    const safeHp = Math.max(0, Math.min(hp, safeMax));
    const fill = track.firstElementChild as HTMLDivElement;
    const value = track.lastElementChild as HTMLSpanElement;
    fill.style.width = `${Math.floor(100 * safeHp / safeMax)}%`;
    value.textContent = `${safeHp}/${maxHp}`;
    track.setAttribute("aria-valuemin", "0");
    track.setAttribute("aria-valuemax", String(maxHp));
    track.setAttribute("aria-valuenow", String(safeHp));
  }

  private tick = 0;

  /** The party panel's keyrace row (keyracing.md D2, 11-15). */
  private partyPanelEl: HTMLElement;
  private raceRowEl: HTMLDivElement | null = null;
  private raceRowSig = "";
  private raceClockEl: HTMLSpanElement | null = null;

  /**
   * Re-render the row's countdown from the freshest remaining-ms value.
   *
   * The server sends REMAINING durations and rebroadcasts only when a race
   * changes, so a clock that waited for packets would freeze at 29:01. This is
   * presentation, the same kind the floor does for movement: it renders a clock,
   * it never decides one — the forfeit stays entirely the server's. It is a
   * separate call rather than part of `renderRaceRow` because the row is
   * signature-guarded: a clock that ticked inside the signature would rebuild the
   * row every second, and a rebuild between mousedown and mouseup eats the click
   * on its Review button (traps/dom-rebuild.md).
   */
  setRaceClock(text: string, tone: "normal" | "low" | "done"): void {
    const el = this.raceClockEl;
    if (!el) return;
    if (el.textContent !== text) el.textContent = text;
    el.classList.toggle("done", tone === "done");
    el.classList.toggle("low", tone === "low");
  }

  /**
   * What "armed" looks like to a player: one row under the party list, not a
   * modal. There is no armed window and no racing window — the tick went green,
   * the consent window is finished, and the next thing a host touches is the
   * door. The registry's vocabulary (armed / racing) never appears in the text.
   *
   * Pass null to remove the row (no race names this party). Signature-guarded
   * like renderParty: the roster rebroadcasts ~10/s in a busy town and the row
   * must not churn under a click (traps/dom-rebuild.md).
   */
  renderRaceRow(info: RaceRowInfo | null): void {
    const sig = info === null
      ? ""
      : `${info.mine.name}|${info.mine.inIt}|${info.theirs.name}|${info.theirs.inIt}|${info.iAmHost}|${info.challenge?.text ?? ""}`;
    if (sig === this.raceRowSig) return;
    this.raceRowSig = sig;
    if (info === null) {
      this.raceRowEl?.remove();
      this.raceRowEl = null;
      this.raceClockEl = null;
      return;
    }
    const row = document.createElement("div");
    row.className = "keyrace";

    const head = document.createElement("div");
    head.className = "keyrace-head";
    const label = document.createElement("span");
    label.className = "keyrace-label";
    label.textContent = "Keyrace active";
    const clock = document.createElement("span");
    // The clock is display only: the server forfeits a side that never walked in
    // whether or not anyone is watching this number, and once both sides are in
    // there is no race clock at all.
    clock.className = "keyrace-clock" + (info.clock.tone === "done" ? " done" : info.clock.tone === "low" ? " low" : "");
    clock.textContent = info.clock.text;
    this.raceClockEl = clock;
    head.append(label, clock);

    const side = (name: string, who: string, inIt: boolean): HTMLDivElement => {
      const line = document.createElement("div");
      line.className = "keyrace-side";
      const dot = document.createElement("span");
      dot.className = "keyrace-dot" + (inIt ? " in" : "");
      dot.textContent = inIt ? "●" : "○";
      const labelEl = document.createElement("span");
      labelEl.className = "keyrace-who";
      labelEl.textContent = who;
      const nameEl = document.createElement("span");
      nameEl.className = "keyrace-name";
      nameEl.textContent = name;
      line.append(dot, labelEl, nameEl);
      return line;
    };

    row.append(head, side(info.mine.name, "yours", info.mine.inIt), side(info.theirs.name, "theirs", info.theirs.inIt));
    // An open challenge: the count, and the way back to the window. This is the
    // surface that survives dismissing the window (traps/stuck-state.md: a
    // dismissed modal with no way back is a dead end).
    if (info.challenge) {
      const chal = document.createElement("div");
      chal.className = "keyrace-challenge";
      const text = document.createElement("span");
      text.textContent = info.challenge.text;
      const review = document.createElement("button");
      review.type = "button";
      review.className = "linkish";
      review.textContent = "Review";
      review.addEventListener("click", () => info.challenge?.onReview());
      chal.append(text, review);
      row.append(chal);
    } else {
      // The one thing the dots cannot say: only the host can open the door, and
      // the door is where the race happens next.
      if (info.iAmHost && !info.mine.inIt) {
        const hint = document.createElement("div");
        hint.className = "keyrace-hint";
        hint.textContent = "You open the door.";
        row.append(hint);
      }
    }

    const prev = this.raceRowEl;
    if (prev) prev.replaceWith(row);
    else this.partyPanelEl.appendChild(row);
    this.raceRowEl = row;
  }

  renderParty(
    party: { id: number; name: string; connected: boolean; keys?: number[] }[],
    hostId?: number,
    actions?: PartyActions,
  ): void {
    // Keys change tick-to-tick (pickup/drop) and this is called every tick
    // from the HUD refresh — signature-guard the rebuild so the list DOM
    // only churns when something visible actually changed (traps/
    // dom-rebuild.md), same discipline as renderAbilities.
    const noParty = party.length <= 1 && !!actions?.onCreate;
    const sig = party.map((p) => `${p.id}:${p.name}:${p.connected ? 1 : 0}:${(p.keys ?? []).join(",")}`).join("|") + "@" + (hostId ?? -1) + `:${actions?.meId ?? -1}:${actions?.leaveLabel ?? ""}:${noParty ? "none" : "list"}`;
    if (sig === this.partySig) return;
    this.partySig = sig;
    this.partyEl.textContent = "";
    const online = party.filter((p) => p.connected).length;
    this.partyCountEl.textContent = noParty ? "—" : `${online}/${party.length}`;
    if (noParty) {
      // "No party" + the one verb, instead of a one-name list wearing a crown.
      const line = document.createElement("div");
      line.className = "line party-none";
      line.textContent = "No party";
      const create = document.createElement("button");
      create.className = "linkish party-create";
      create.textContent = actions!.createLabel ?? "Create party";
      create.addEventListener("click", () => actions!.onCreate!());
      this.partyEl.append(line, create);
      return;
    }
    for (const p of party) {
      const li = document.createElement("div");
      li.className = "line party-line";
      const sw = document.createElement("span");
      sw.className = "swatch";
      sw.style.background = playerColor(p.id);
      const name = document.createElement("span");
      name.className = "name";
      name.textContent = p.name;
      if (p.id === hostId) {
        const crown = document.createElement("span");
        crown.className = "party-crown";
        crown.textContent = "♛";
        crown.title = "Party leader";
        li.append(sw, crown, name);
      } else {
        li.append(sw, name);
      }
      // Every key the member holds, beside their name (keyLetter's A/B/C —
      // the door labels use the same letters). The row WRAPS: a key-hoarding
      // run is the case this is for, and the rail has no vertical scroll.
      for (const keyId of p.keys ?? []) {
        const chip = document.createElement("span");
        chip.className = "party-key";
        chip.textContent = keyLetter(keyId);
        chip.title = `Key ${keyLetter(keyId)}`;
        li.appendChild(chip);
      }
      const dot = document.createElement("span");
      dot.className = "dot";
      dot.style.color = p.connected ? "#5fbf5f" : "#7a7a82";
      dot.textContent = p.connected ? "●" : "○";
      li.append(dot);
      if (actions?.openMenu) {
        const menuItems: { label: string; run: () => void }[] = [];
        if (p.id !== actions.meId && hostId === actions.meId && actions.onPromote) {
          menuItems.push({ label: "Promote to host", run: () => actions.onPromote!(p.id) });
        }
        if (p.id === actions.meId && actions.onLeave) {
          menuItems.push({ label: actions.leaveLabel ?? "Leave party", run: actions.onLeave });
        }
        if (menuItems.length) {
          li.classList.add("party-action-row");
          li.title = "Right-click for party actions";
          li.addEventListener("contextmenu", (ev) => {
            ev.preventDefault();
            actions.openMenu!(ev.clientX, ev.clientY, p.name, menuItems);
          });
        }
      }
      this.partyEl.appendChild(li);
    }
    if (actions?.onLeave) {
      const leave = document.createElement("button");
      leave.className = "linkish party-leave";
      leave.textContent = actions.leaveLabel ?? "Leave party";
      leave.addEventListener("click", actions.onLeave);
      this.partyEl.appendChild(leave);
    }
  }

  /** The buff bar. Tiles are rebuilt only when the SET of active effects
   *  changes; the countdown text updates in place every tick, so a per-tick
   *  call can never eat a press (docs/traps/dom-rebuild.md). */
  renderBuffs(entries: BuffEntry[]): void {
    this.buffBarEl.hidden = entries.length === 0;
    const sig = entries.map((e) => `${e.id}${e.debuff ? "!" : ""}`).join("|");
    if (sig !== this.buffSig) {
      this.buffSig = sig;
      this.buffBarEl.textContent = "";
      this.buffTiles = [];
      this.buffTips = [];
      for (const e of entries) {
        // A wrapper around the tile so the hover card can sit at full opacity
        // while the tile itself sits at 50% (a parent's opacity multiplies
        // into every child, so the card cannot live inside the tile).
        const wrap = document.createElement("div");
        wrap.className = "buff-wrap";
        const tile = document.createElement("div");
        tile.className = "buff-tile";
        const icon = document.createElement("span");
        icon.className = "buff-icon";
        icon.innerHTML = buffIconSvg(e.icon);
        const mag = document.createElement("span");
        mag.className = "buff-mag";
        const time = document.createElement("span");
        time.className = "buff-time";
        tile.append(icon, mag, time);
        // The dock's own tooltip, not a `title`: the native one takes half a
        // second to appear and cannot be styled (the user's call on 10-06).
        const tip = createAbilityTooltip();
        wrap.append(tile, tip);
        this.buffBarEl.appendChild(wrap);
        this.buffTiles.push(tile);
        this.buffTips.push(tip);
      }
    }
    entries.forEach((e, index) => {
      const tile = this.buffTiles[index];
      const tip = this.buffTips[index];
      if (!tile || !tip) return;
      tile.classList.toggle("debuff", e.debuff);
      (tile.querySelector(".buff-mag") as HTMLElement).textContent = e.magnitudeText;
      (tile.querySelector(".buff-time") as HTMLElement).textContent = e.timeText;
      updateAbilityTooltip(tip, e.label, e.hint, `${e.magnitudeText} · ${e.timeText} left`);
    });
  }

  renderAbilities(entries: AbilityEntry[]): void {
    // Cooldowns change every tick. Keep the dock button nodes stable and
    // update their fields in place so an observation cannot rebuild the DOM
    // between mousedown and mouseup (docs/traps/dom-rebuild.md).
    this.currentAbilities = entries;
    const sig = entries.map((a) => `${a.id}:${a.group}`).join("|");
    if (sig !== this.abilitySig) {
      this.abilitySig = sig;
      this.abilitiesEl.textContent = "";
      this.abilityButtons = [];
      let lastGroup: AbilityEntry["group"] | null = null;
      entries.forEach((a, index) => {
        if (lastGroup !== null && a.group !== lastGroup) {
          const split = document.createElement("span");
          split.className = "ability-split";
          this.abilitiesEl.appendChild(split);
        }
        lastGroup = a.group;
        const btn = document.createElement("button");
        btn.className = "ability";
        btn.dataset.index = String(index);
        const wrap = document.createElement("span");
        wrap.className = "ability-wrap";
        const cd = document.createElement("span");
        cd.className = "cooldown";
        const key = document.createElement("span");
        key.className = "key";
        const icon = document.createElement("span");
        icon.className = "ability-icon";
        const count = document.createElement("span");
        count.className = "cooldown-count";
        btn.append(cd, icon, key, count);
        // The action fires on pointer-DOWN (the canvas primary action uses the
        // same rule — viewport.ts R1), never on click: a press whose release
        // lands OUTSIDE the window (the dock sits at the window's bottom edge —
        // a small slip blurs the page) gets a retargeted mouseup but no click
        // dispatched, so a click-driven button dies exactly there. Down is
        // inescapable — and instant, which combat pacing wants (docs/traps/dom-rebuild.md).
        btn.addEventListener("pointerdown", (ev) => {
          if (ev.button !== 0) return;
          ev.preventDefault();
          this.currentAbilities[index]?.onUse?.();
        });
        wrap.append(btn, createAbilityTooltip());
        this.abilitiesEl.appendChild(wrap);
        this.abilityButtons.push(btn);
      });
    }
    entries.forEach((a, index) => {
      const btn = this.abilityButtons[index];
      if (!btn) return;
      btn.disabled = !a.enabled;
      btn.classList.toggle("active", a.active === true);
      btn.classList.toggle("armed", a.armed === true);
      btn.classList.toggle("cooling", (a.cooldownSeconds ?? 0) > 0);
      btn.classList.toggle("class-ability", a.group === "class");
      btn.classList.toggle("gatestone-ability", a.group === "gatestone");
      btn.classList.toggle("map-ability", a.group === "map");
      btn.style.setProperty("--cooldown", `${Math.max(0, Math.min(100, a.cooldownPct ?? 0))}%`);
      (btn.querySelector(".key") as HTMLElement).textContent = a.key ?? "";
      if (btn.dataset.icon !== a.icon) {
        (btn.querySelector(".ability-icon") as HTMLElement).innerHTML = dockIconSvg(a.icon);
        btn.dataset.icon = a.icon;
      }
      (btn.querySelector(".cooldown-count") as HTMLElement).textContent =
        a.cooldownSeconds && a.cooldownSeconds > 0 ? String(a.cooldownSeconds) : "";
      btn.setAttribute("aria-label", a.key ? `${a.label} (key ${a.key})` : a.label);
      const tip = btn.parentElement?.querySelector(".ability-tooltip") as HTMLElement | null;
      if (tip) {
        const state = a.cooldownSeconds && a.cooldownSeconds > 0
          ? `Ready in ${a.cooldownSeconds}s`
          : a.enabled ? "Ready" : "Unavailable";
        updateAbilityTooltip(tip, a.key ? `${a.label} · ${a.key}` : a.label, a.sub ?? "", state);
      }
    });
  }

  /** Armour down the centre, weapon left and shield right. Filled cells show
   *  item art with names/stats on hover; empty sockets show the slot outline.
   *  Left-click a filled cell = unequip; right-click = menu.
   *  Same diff-rebuild discipline as the inventory grid. */
  renderEquipment(slots: EquipmentSlot[], stats?: GearCombatStats): void {
    this.currentEquipment = slots;
    this.currentEquipmentStats = stats;
    const sig = JSON.stringify([slots, stats]);
    if (sig === this.equipmentSig) return;
    this.equipmentSig = sig;
    this.equipmentEl.textContent = "";
    const grid = document.createElement("div");
    grid.className = "equip-grid";
    for (const s of slots) {
      const el = document.createElement("div");
      const def = s.itemDef ?? (s.itemKind !== null ? itemByKind(s.itemKind) : undefined);
      el.className = "equip-cell" + (def ? " filled" : " empty") +
        (s.slot === "weapon" || s.slot === "shield" ? " equip-hand" : " equip-armour");
      el.dataset.slot = s.slot;
      el.setAttribute("aria-label", `${s.label}: ${def?.name ?? "empty"}`);
      const icon = document.createElement("div");
      icon.className = "icon";
      if (def && this.onDrawIcon) {
        // Reuse the inventory icon painter: a minimal entry carrying only
        // the icon id (items are always canon-tinted).
        this.onDrawIcon(icon, { label: def.name, primary: "", icon: def.icon, menu: [] });
      } else if (!def) icon.innerHTML = equipmentSlotIcon(s.slot);
      const name = document.createElement("div");
      name.className = "eq-name";
      name.textContent = def ? def.name : "";
      const slotName = document.createElement("div");
      slotName.className = "eq-slot";
      slotName.textContent = s.label;
      el.append(icon, name, slotName);
      if (def) attachItemStats(el, def, s.efficiencyPct);
      else el.title = `${s.label}: empty`;
      const remove = (): void => {
        const current = this.currentEquipment.find((entry) => entry.slot === s.slot);
        if (current?.itemKind != null) window.dispatchEvent(new CustomEvent("pd-unequip", { detail: s.slot }));
      };
      el.addEventListener("pointerdown", (ev) => {
        if (ev.button !== 0) return;
        traceItemClick("equipment-primary", { slot: s.slot, itemKind: s.itemKind, connected: el.isConnected });
        ev.preventDefault();
        ev.stopPropagation();
        remove();
      });
      el.addEventListener("click", (ev) => {
        ev.stopPropagation();
        if (ev.detail === 0) remove(); // keyboard activation from the stats popover
      });
      el.addEventListener("contextmenu", (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        if (def) {
          this.onItemMenu?.(ev.clientX, ev.clientY, {
            label: def.name,
            icon: def.icon,
            primary: "Remove",
            menu: [
              { label: "Remove", run: () => window.dispatchEvent(new CustomEvent("pd-unequip", { detail: s.slot })) },
              // Drop the WORN piece (DropItem cannot name worn gear — the
              // game layer sends remove + drop, in order, at the slot the
              // remove lands it in: the pocket's end).
              { label: "Drop", run: () => window.dispatchEvent(new CustomEvent("pd-drop-equipped", { detail: s.slot })) },
              { label: "Examine" },
            ],
          });
        }
      });
      grid.appendChild(el);
    }
    this.equipmentEl.appendChild(grid);
    if (stats) this.equipmentEl.appendChild(gearStatsPanel(stats));
    traceItemClick("equipment-rebuild", slots.map(slot => ({ slot: slot.slot, itemKind: slot.itemKind })));
  }

  renderInventory(entries: InventoryEntry[]): void {
    this.currentEntries = entries;
    // Diff on entry data (functions are dropped by JSON, and identity is
    // carried by label/sub/primary/icon) — no rebuild, no DOM churn.
    const sig = JSON.stringify(entries);
    if (sig === this.inventorySig) return;
    this.inventorySig = sig;
    this.invCountEl.textContent = String(entries.length);
    this.inventoryEl.textContent = "";
    const layout = gridCells(entries);
    const grid = document.createElement("div");
    grid.className = "inventory-grid";
    // 1fr columns: the grid fills the panel width so no trailing gutter
    // appears after the 4th column (the "1,2,3,4 <padding>" artifact).
    grid.style.gridTemplateColumns = `repeat(${layout.cols}, 1fr)`;
    let slot = 0;
    for (const cell of layout.cells) {
      const el = document.createElement("div");
      if (!cell) {
        el.className = "cell empty";
        el.dataset.slot = String(slot);
        grid.appendChild(el);
        slot++;
        continue;
      }
      const e = cell.entry;
      el.className = "cell item" + (e.disabled ? " dim" : "");
      el.dataset.slot = String(slot);
      el.dataset.index = String(cell.index);
      const icon = document.createElement("div");
      icon.className = "icon";
      if (e.icon && this.onDrawIcon) {
        this.onDrawIcon(icon, e);
      }
      const cap = document.createElement("div");
      cap.className = "cap";
      cap.textContent = e.label;
      const prim = document.createElement("div");
      prim.className = "primary";
      prim.textContent = e.primary;
      el.append(icon, cap, prim);
      if (e.itemDef) attachItemStats(el, e.itemDef, e.efficiencyPct);
      else el.title = e.sub ? `${e.label}: ${e.sub}` : e.label;
      el.addEventListener("pointerdown", (ev) => {
        if (ev.button !== 0) return;
        this.beginDrag(el, ev, e, cell.index);
      });
      el.addEventListener("click", (ev) => {
        ev.stopPropagation();
        // Mouse activation belongs to the press/release gesture: a server
        // update can remove this node before the browser dispatches click.
        traceItemClick("inventory-native-click", { detail: ev.detail, index: cell.index, entry: debugEntry(e),
          handledAs: ev.detail === 0 ? "keyboard-primary" : "ignored-mouse-click-after-release" });
        if (ev.detail === 0) this.currentEntries[cell.index]?.onPrimary?.();
      });
      el.addEventListener("contextmenu", (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        this.onItemMenu?.(ev.clientX, ev.clientY, e);
      });
      grid.appendChild(el);
      slot++;
    }
    this.inventoryEl.appendChild(grid);
    traceItemClick("inventory-rebuild", { entries: entries.map(debugEntry), duringPress: !!this.drag,
      pressedSourceConnected: this.drag?.source.isConnected ?? null });
  }

  // ---- drag-and-drop (M4 HUD) -------------------------------------------

  /** Pointer-drag an inventory item: within the grid → reorder (real sim op
   *  via onReorder), out of the panel → drop at your feet.
   *  Left-click and right-click keep their meanings; a drag only starts
   *  after 150ms held, 6px of movement, and leaving the pressed cell. */
  private beginDrag(
    el: HTMLElement,
    ev: PointerEvent,
    e: InventoryEntry,
    fromIndex: number,
  ): void {
    ev.preventDefault();
    this.endDrag(true);
    this.drag = {
      source: el,
      entry: e,
      fromIndex,
      entriesAtPress: this.currentEntries,
      bounds: el.getBoundingClientRect(),
      pointerId: ev.pointerId,
      startedAt: performance.now(),
      startX: ev.clientX,
      startY: ev.clientY,
      lastX: ev.clientX,
      lastY: ev.clientY,
      active: false,
      inPanel: true,
      ghost: null,
      dropTarget: null,
    };
    traceItemClick("inventory-press", { entry: debugEntry(e), fromIndex, x: ev.clientX, y: ev.clientY,
      bounds: this.drag.bounds, sourceConnected: el.isConnected });
    const onMove = (me: PointerEvent) => {
      if (me.pointerId === this.drag?.pointerId) this.onDragMove(me);
    };
    const onUp = (ue: PointerEvent) => {
      if (ue.button !== 0 || ue.pointerId !== this.drag?.pointerId) return;
      if (!this.drag) return;
      this.drag.lastX = ue.clientX;
      this.drag.lastY = ue.clientY;
      this.endDrag();
    };
    const onCancel = (ce: PointerEvent): void => {
      if (ce.pointerId === this.drag?.pointerId) this.endDrag(true);
    };
    const onBlur = (): void => this.endDrag(true);
    // If the pointer left early and is now stationary, start the drag when
    // the hold threshold expires. Keep window listeners across DOM rebuilds.
    const holdTimer = setTimeout(() => {
      if (this.drag) this.onDragMove({ clientX: this.drag.lastX, clientY: this.drag.lastY });
    }, 150);
    this.clearDragListeners = () => {
      clearTimeout(holdTimer);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      window.removeEventListener("blur", onBlur);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    window.addEventListener("blur", onBlur);
  }

  private onDragMove(ev: { clientX: number; clientY: number }): void {
    const d = this.drag;
    if (!d) return;
    d.lastX = ev.clientX;
    d.lastY = ev.clientY;
    if (!d.active) {
      if (d.entry.keyId === undefined && d.entry.stoneId === undefined && d.entry.itemKind === undefined) return;
      if (performance.now() - d.startedAt < 150) return;
      if (Math.hypot(ev.clientX - d.startX, ev.clientY - d.startY) < 6) return;
      const r = d.bounds;
      if (ev.clientX >= r.left && ev.clientX < r.right && ev.clientY >= r.top && ev.clientY < r.bottom) return;
      traceItemClick("inventory-drag-start", { entry: debugEntry(d.entry), thresholdPx: 6,
        holdThresholdMs: 150, heldMs: performance.now() - d.startedAt,
        dx: ev.clientX - d.startX, dy: ev.clientY - d.startY });
      d.active = true;
      d.source.classList.add("dragging");
      d.ghost = d.source.cloneNode(true) as HTMLElement;
      d.ghost.classList.remove("dragging");
      d.ghost.classList.add("cell-ghost");
      document.body.appendChild(d.ghost);
      this.updateGhost(ev);
      this.setDropHint(droppable(d.entry) ? "Release to drop at your feet" : "This item can't be dropped");
    }
    if (!d.active) return;
    d.lastX = ev.clientX;
    d.lastY = ev.clientY;
    this.updateGhost(ev);
    // The cell under the cursor (the ghost is pointer-events: none).
    const under = document.elementFromPoint(ev.clientX, ev.clientY);
    const cell = under?.closest?.(".inventory-grid .cell.item") as HTMLElement | null;
    if (cell !== d.dropTarget) {
      d.dropTarget?.classList.remove("drop-target");
      cell?.classList.add("drop-target");
      d.dropTarget = cell ?? null;
    }
    d.inPanel = !!under && this.inventoryEl.contains(under);
    this.dropHint.style.display = d.inPanel ? "none" : "block";
    this.updateDropHintPos(ev);
  }

  private updateGhost(ev: { clientX: number; clientY: number }): void {
    if (!this.drag?.ghost) return;
    this.drag.ghost.style.left = `${ev.clientX + 12}px`;
    this.drag.ghost.style.top = `${ev.clientY + 12}px`;
  }

  private endDrag(cancelled = false): void {
    const d = this.drag;
    if (!d) return;
    this.clearDragListeners?.();
    this.clearDragListeners = null;
    this.drag = null;
    d.source.classList.remove("dragging");
    d.dropTarget?.classList.remove("drop-target");
    d.ghost?.remove();
    this.dropHint.style.display = "none";
    const heldMs = performance.now() - d.startedAt;
    traceItemClick("inventory-release", { cancelled, dragging: d.active, heldMs, entry: debugEntry(d.entry),
      x: d.lastX, y: d.lastY, dx: d.lastX - d.startX, dy: d.lastY - d.startY,
      sourceConnected: d.source.isConnected });
    if (cancelled) {
      traceItemClick("inventory-action-skipped", { reason: "gesture-cancelled" });
      return;
    }
    // Resolve the pressed item in the latest inventory: updates may move
    // its pocket slot while the button is held. Never use the old slot's
    // callback, which could now use or drop a different item.
    const current = this.pressedEntry(d);
    if (!current) {
      traceItemClick("inventory-action-skipped", { reason: "pressed-item-missing-or-identical-copy-count-changed", entries: this.currentEntries.map(debugEntry) });
      return;
    }
    if (!d.active) {
      const r = d.bounds;
      const inside = d.lastX >= r.left && d.lastX < r.right && d.lastY >= r.top && d.lastY < r.bottom;
      // A quick click may release outside the cell as the mouse moves on.
      // Pointer events preserve fractional coordinates at cell edges.
      const activate = inside || heldMs < 150;
      traceItemClick(activate ? "inventory-primary" : "inventory-action-skipped", {
        entry: debugEntry(current.entry), currentIndex: current.index, hasAction: !!current.entry.onPrimary,
        reason: inside ? "released-within-original-cell" : activate ? "quick-release-before-drag-threshold" : "released-outside-original-cell", bounds: r });
      if (activate) current.entry.onPrimary?.();
      return;
    }
    // Released over a grid cell (filled OR empty) → reorder to that slot;
    // released outside the panel → drop at your feet.
    const under = document.elementFromPoint(d.lastX, d.lastY);
    const cell = under?.closest?.(".inventory-grid .cell") as HTMLElement | null;
    if (cell) {
      traceItemClick("inventory-reorder", { from: current.index, to: Number(cell.dataset.slot ?? -1), entry: debugEntry(current.entry) });
      this.reorderTo(d, Number(cell.dataset.slot ?? -1));
    } else if (!under || !this.inventoryEl.contains(under)) {
      traceItemClick("inventory-drop", { entry: debugEntry(current.entry), currentIndex: current.index });
      if (current.entry.stoneId !== undefined) {
        window.dispatchEvent(new CustomEvent("pd-drop", { detail: current.entry.stoneId }));
      } else if (current.entry.keyId !== undefined) {
        window.dispatchEvent(new CustomEvent("pd-drop-key", { detail: current.entry.keyId }));
      } else if (current.entry.itemSlot !== undefined) {
        window.dispatchEvent(new CustomEvent("pd-drop-item", { detail: current.entry.itemSlot }));
      }
    } else {
      traceItemClick("inventory-action-skipped", { reason: "drag-released-in-panel-without-target-cell" });
    }
  }

  private pressedEntry(d: NonNullable<Hud["drag"]>): { entry: InventoryEntry; index: number } | null {
    const matches = (entry: InventoryEntry): boolean => {
      if (d.entry.keyId !== undefined) return entry.keyId === d.entry.keyId;
      if (d.entry.stoneId !== undefined) return entry.stoneId === d.entry.stoneId;
      if (d.entry.itemKind !== undefined) return entry.itemKind === d.entry.itemKind && (entry.itemCls ?? "equipment") === (d.entry.itemCls ?? "equipment");
      return entry.label === d.entry.label && entry.primary === d.entry.primary;
    };
    const before = d.entriesAtPress.filter(matches);
    const current = this.currentEntries.map((entry, index) => ({ entry, index })).filter(({ entry }) => matches(entry));
    // Identical pocket copies have no entity ids. If one disappears mid-
    // press, its surviving sibling cannot safely inherit that gesture.
    if (before.length !== current.length) return null;
    const occurrence = d.entriesAtPress.slice(0, d.fromIndex).filter(matches).length;
    return current[occurrence] ?? null;
  }

  /** Rebuild the desired keys/carried/items order after a drag and hand it
   *  to the sim (onReorder) — the next observation re-renders the grid.
   *  All three arrays travel in one op. */
  private reorderTo(d: NonNullable<Hud["drag"]>, toSlot: number): void {
    const entries = this.currentEntries;
    const current = this.pressedEntry(d);
    if (!current) return;
    const layout = gridCells(entries);
    const toEntry = layout.cells.slice(0, Math.max(0, toSlot)).filter(Boolean).length;
    const next = reorderEntries(entries, current.index, toEntry);
    const keys: number[] = [];
    const carried: number[] = [];
    const items: PocketItem[] = [];
    for (const e of next) {
      if (e.keyId !== undefined) keys.push(e.keyId);
      if (e.stoneId !== undefined) carried.push(e.stoneId);
      if (e.itemKind !== undefined) items.push({ cls: e.itemCls ?? "equipment", kind: e.itemKind });
    }
    this.onReorder?.(keys, carried, items);
  }

  private dropHint = (() => {
    const el = document.createElement("div");
    el.id = "drop-hint";
    document.body.appendChild(el);
    return el;
  })();

  private updateDropHintPos(ev: { clientX: number; clientY: number }): void {
    this.dropHint.style.left = `${ev.clientX + 14}px`;
    this.dropHint.style.top = `${ev.clientY + 14}px`;
  }

  private setDropHint(text: string): void {
    this.dropHint.textContent = text;
  }

  /** Hook so the game can open a context menu for an inventory item. */
  onItemMenu: ((x: number, y: number, e: InventoryEntry) => void) | null = null;

  /** M4 HUD drag-reorder: the sim-facing order of held keys, carried
   *  stones, and unequipped items after a drag. game.ts turns this into
   *  the real op (ReorderInventory carries all three lists). */
  onReorder: ((keys: number[], carried: number[], items: PocketItem[]) => void) | null = null;

  /** Phase 2 hook: draw the real item sprite into a cell's icon slot (set
   *  by game.ts once the item assets load). The entry's ownerId tints
   *  carried stones. Until then the icon slot stays empty; it never invents
   *  a caption or letter that could be mistaken for item art. */
  onDrawIcon: ((slot: HTMLElement, entry: InventoryEntry) => void) | null = null;

  /** Repaint already-mounted item cells after the shared atlas finishes
   * loading. The data signature is unchanged, so ordinary observation
   * projection would otherwise leave the old empty/placeholder cell mounted. */
  invalidateIcons(): void {
    this.equipmentSig = "";
    this.inventorySig = "";
    this.renderEquipment(this.currentEquipment, this.currentEquipmentStats);
    this.renderInventory(this.currentEntries);
  }

  showBanner(text: string): void {
    this.bannerEl.textContent = text;
    this.bannerEl.classList.add("show");
  }

  hideBanner(): void {
    this.bannerEl.classList.remove("show");
  }
}

/** Stones, keys, and pocket items (equipment + consumables — amended
 *  2026-10-25, DropItem) can be dragged out of the panel and dropped at
 *  the feet.
 *  Worn gear drops through the Equipment panel's own Drop verb (unequip +
 *  drop, sent in order). */
function droppable(e: InventoryEntry): boolean {
  return e.stoneId !== undefined || e.keyId !== undefined || e.itemSlot !== undefined;
}

function debugEntry(e: InventoryEntry): unknown {
  return { label: e.label, primary: e.primary, itemKind: e.itemKind, itemCls: e.itemCls,
    itemSlot: e.itemSlot, keyId: e.keyId, stoneId: e.stoneId, disabled: e.disabled ?? false };
}

/** The consumable's one-line effect summary (HUD sub-text). Ticks →
 *  seconds at 20 tps (display only — the client may use floats). */
function consumableSub(def: ConsumableDef): string {
  if (def.type === "heal") return `+${def.amount} HP`;
  const what = def.buffType === "damage_taken" ? "damage taken" : def.buffType ?? "?";
  const mult = def.magnitude !== null ? (def.magnitude / 100).toString() : "?";
  const secs = def.duration !== null ? Math.round(def.duration / 20) : "?";
  return `${what} ×${mult} for ${secs}s`;
}

/** Build the Equipment panel slots from the observation (sim order:
 *  weapon/head/chest/legs). */
export function buildEquipment(obs: Observation, meId: number): EquipmentSlot[] {
  const me = obs.players.find((p) => p.id === meId)!;
  return EQUIP_SLOTS.map((slot) => ({
    slot,
    label: SLOT_LABELS[slot],
    itemKind: me.equipment[slot] ?? null,
    itemDef: me.itemDetails?.find((d) => d.kind === me.equipment[slot]),
    efficiencyPct: (() => {
      const def = me.itemDetails?.find((d) => d.kind === me.equipment[slot]) ?? itemByKind(me.equipment[slot] ?? -1);
      return def ? itemEfficiency(def, me.combatStats) : undefined;
    })(),
  }));
}

/** Build the inventory entries from the observation. */
export function buildInventory(obs: Observation, meId: number, playerName?: (id: number) => string): InventoryEntry[] {
  const me = obs.players.find((p) => p.id === meId)!;
  const entries: InventoryEntry[] = [];
  for (const k of me.keys) {
    const id = k;
    entries.push({
      label: `Key ${keyLetter(k)}`,
      sub: "opens the matching door",
      primary: "Drop",
      icon: "key",
      keyId: k,
      onPrimary: () => window.dispatchEvent(new CustomEvent("pd-drop-key", { detail: id })),
      menu: [
        { label: "Drop", run: () => window.dispatchEvent(new CustomEvent("pd-drop-key", { detail: id })) },
        { label: "Examine" },
      ],
    });
  }
  // The pocket is class-tagged (design/loot.md): one grid of (class, kind)
  // pairs — equipment equips, consumables USE (the Use op resolves the
  // entry at its pocket index; the effect lines are the sim's own numbers,
  // never re-derived here).
  me.items.forEach((it, pocketIndex) => {
    // The drop verb: the DropItem op names the POCKET INDEX, like UseItem
    // (the sim resolves the (class, kind) entry at that slot).
    const drop = () => window.dispatchEvent(new CustomEvent("pd-drop-item", { detail: pocketIndex }));
    if (it.cls === "equipment") {
      const def = me.itemDetails?.find((d) => d.kind === it.kind) ?? itemByKind(it.kind);
      if (!def) return; // unknown kind — the sim never produces one, but the HUD must not break
      entries.push({
        label: def.name,
        sub: SLOT_LABELS[def.slot],
        primary: "Equip",
        icon: def.icon,
        itemKind: it.kind,
        itemCls: "equipment",
        itemDef: def,
        efficiencyPct: itemEfficiency(def, me.combatStats),
        itemSlot: pocketIndex,
        onPrimary: () => window.dispatchEvent(new CustomEvent("pd-equip", { detail: it.kind })),
        menu: [
          { label: "Equip", run: () => window.dispatchEvent(new CustomEvent("pd-equip", { detail: it.kind })) },
          { label: "Drop", run: drop },
          { label: "Examine" },
        ],
      });
    } else {
      const def = consumableById(it.kind);
      if (!def) return;
      entries.push({
        label: def.name,
        sub: consumableSub(def),
        primary: "Use",
        icon: def.icon,
        itemKind: it.kind,
        itemCls: "consumable",
        itemSlot: pocketIndex,
        // The detail is the POCKET INDEX (me.items[pocketIndex]) — the Use
        // op names the slot, not the kind; the sim resolves the entry.
        onPrimary: () => window.dispatchEvent(new CustomEvent("pd-use-item", { detail: pocketIndex })),
        menu: [
          { label: "Use", run: () => window.dispatchEvent(new CustomEvent("pd-use-item", { detail: pocketIndex })) },
          { label: "Drop", run: drop },
          { label: "Examine" },
        ],
      });
    }
  });
  for (const s of me.carried) {
    const mine = s.ownerId === meId;
    const ownerName = playerName?.(s.ownerId) ?? obs.players.find((p) => p.id === s.ownerId)?.name ?? "?";
    const label = s.kind === "group" ? "Group gatestone" : `Gatestone (${ownerName})`;
    const id = s.id;
    entries.push({
      label,
      sub: s.kind === "group" ? "the party's stone" : mine ? "yours" : "borrowed, not yours",
      primary: "Drop",
      icon: s.kind === "group" ? "gatestone-group" : "gatestone-personal",
      ownerId: s.ownerId,
      stoneId: s.id,
      onPrimary: () => window.dispatchEvent(new CustomEvent("pd-drop", { detail: id })),
      menu: [
        { label: "Drop", run: () => window.dispatchEvent(new CustomEvent("pd-drop", { detail: id })) },
        { label: "Examine" },
      ],
    });
  }
  return entries;
}

/** Build the shared dock: the character's three class abilities, three
 * gatestone actions, and the Dungeon Map in its own group. The observation
 * owns every readiness / active-state read; callbacks only express intent. */
export function buildAbilities(
  obs: Observation,
  meId: number,
  tick: number,
  armedAbility: number | null = null,
): AbilityEntry[] {
  const me = obs.players.find((p) => p.id === meId)!;
  const classDef = CLASS_DEFS[me.classKind];
  const level = Math.max(1, Math.min(5, me.level)) - 1;
  const classEntries: AbilityEntry[] = classDef.abilities.map((ability, slot) => {
    const row = ability.levels[level];
    const remaining = Math.max(0, (me.abilityReadyAt[slot] ?? 0) - tick);
    const total = Math.max(1, row?.cooldown ?? 1);
    const charged = me.pendingHit?.slot === slot;
    // Phoenix Teleport is gated by a DEATH, not by a clock: the observation's
    // phoenixTile IS the availability (null = never died here, or the last
    // death is already spent), so the dock lights on it. The store keeps the
    // kind's cooldown row at 1 tick precisely so `remaining` never denies a
    // charge the hero earned by dying.
    const phoenixDark = ability.kind === "phoenix-teleport" && !me.phoenixTile;
    const active =
      charged ||
      (ability.kind === "phoenix-teleport" && !phoenixDark) ||
      (ability.kind === "speed-buff" && !!me.speedBuff && tick < me.speedBuff.until) ||
      (ability.kind === "damage-immune" && tick < me.damageImmuneUntil) ||
      (ability.kind === "damage-reflect" && tick < me.reflectUntil);
    return {
      id: `class-${ability.id}`,
      label: ability.name,
      icon: ability.kind,
      sub: phoenixDark
        ? "Dark until the first time you die on this floor."
        : abilityHint(ability.kind),
      enabled: remaining === 0 && !charged && !phoenixDark,
      group: "class",
      key: String(slot + 1),
      cooldownPct: (remaining * 100) / total,
      cooldownSeconds: Math.ceil(remaining / 20),
      active,
      armed: armedAbility === ability.id,
      onUse: () => window.dispatchEvent(new CustomEvent("pd-ability", { detail: ability.id })),
    };
  });
  const gatestones: AbilityEntry[] = [
    {
      id: "gatestone-generate",
      label: "Generate gatestone",
      icon: "summon-pgt",
      sub: me.pgs === "inventory" ? "Your stone is already in hand." : me.pgs === "deployed" ? "Call your stone back to you." : "Summon your personal gatestone.",
      enabled: me.pgs !== "inventory",
      group: "gatestone",
      key: "4",
      onUse: () => window.dispatchEvent(new CustomEvent("pd-generate")),
    },
    {
      id: "gatestone-personal",
      label: "Teleport: personal",
      icon: "teleport-pgt",
      sub: me.pgs === "deployed" ? "Jump to your stone. It vanishes afterward." : me.pgs === "inventory" ? "Your stone is in hand, not on the floor." : "Summon and place your stone first.",
      enabled: me.pgs === "deployed",
      group: "gatestone",
      key: "5",
      onUse: () => window.dispatchEvent(new CustomEvent("pd-teleport", { detail: "personal" })),
    },
    {
      id: "gatestone-group",
      label: "Teleport: group",
      icon: "teleport-ggs",
      sub: "Jump to the party's gatestone.",
      enabled: true,
      group: "gatestone",
      key: "6",
      onUse: () => window.dispatchEvent(new CustomEvent("pd-teleport", { detail: "group" })),
    },
  ];
  const map: AbilityEntry = {
    id: "dungeon-map",
    label: "Dungeon Map",
    icon: "dungeon-map",
    sub: "View the rooms your party has explored.",
    enabled: true,
    group: "map",
    key: "M",
    onUse: () => window.dispatchEvent(new CustomEvent("pd-map-open")),
  };
  return [...classEntries, ...gatestones, map];
}

/** The same dock while standing in the hub. Its positions remain stable so
 * entering a floor does not make the interface jump; the hub has no sim, so
 * all seven actions are visibly unavailable. */
export function hubAbilities(classKind?: HeroClass): AbilityEntry[] {
  const classEntries: AbilityEntry[] = [0, 1, 2].map((slot) => {
    const ability = classKind ? CLASS_DEFS[classKind].abilities[slot] : undefined;
    return {
      id: `class-${slot}`,
      label: ability?.name ?? "Ability",
      icon: ability?.kind ?? "empty",
      sub: ability ? abilityHint(ability.kind) : "Enter a floor to use abilities.",
      enabled: false,
      group: "class",
      key: String(slot + 1),
    };
  });
  return [
    ...classEntries,
    { id: "gatestone-generate", label: "Generate gatestone", icon: "summon-pgt", sub: "Enter a floor to use gatestones.", enabled: false, group: "gatestone", key: "4" },
    { id: "gatestone-personal", label: "Teleport: personal", icon: "teleport-pgt", sub: "Enter a floor to use gatestones.", enabled: false, group: "gatestone", key: "5" },
    { id: "gatestone-group", label: "Teleport: group", icon: "teleport-ggs", sub: "Enter a floor to use gatestones.", enabled: false, group: "gatestone", key: "6" },
    { id: "dungeon-map", label: "Dungeon Map", icon: "dungeon-map", sub: "Enter a floor to view its map.", enabled: false, group: "map", key: "M" },
  ];
}
